/** Auth — mirrors BE `LoginResultDto` / `UserProfileDto` (camelCase JSON). */
import type {
  BatchStatus,
  DemandStatus,
  EnablementStatus,
  InventoryStatus,
  ProductionOrderStatus,
  PurchaseOrderStatus,
  PurchaseRequisitionStatus,
  QcStatus,
  SalesFulfillmentType,
  SalesOrderStatus,
  ShipmentStatus,
  TxnStatus,
  VersionStatus,
} from "./domainCodes";

/** Role at current plant — BE `RoleRefDto`. */
export type ApiRoleRef = {
  code: string;
  name: string;
};

export type ApiUserProfile = {
  id: string;
  username: string;
  fullName: string;
  plantId: string | null;
  plantCode: string | null;
  plantName?: string | null;
  roles: ApiRoleRef[];
  permissions: string[];
  email?: string | null;
  phone?: string | null;
};

export type ApiUpdateMyProfileRequest = {
  fullName: string;
  email?: string | null;
  phone?: string | null;
};

export type ApiChangeMyPasswordRequest = {
  currentPassword: string;
  newPassword: string;
};

export type ApiLoginResult = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  user: ApiUserProfile;
};

export type ApiLoginRequest = {
  username: string;
  password: string;
  plantCode?: string | null;
};

export type ApiRefreshRequest = {
  refreshToken: string;
};

export type ApiErrorBody = {
  errorCode?: string;
  message?: string;
};

/** Standard list envelope — mirrors BE `PagedResult<T>`. */
export type ApiPagedResult<T> = {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type ApiListQuery = {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
  sort?: string;
};

/** MDM item category — mirrors BE `ItemCategoryDto`. */
export type ApiItemCategory = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  parentCode: string | null;
  parentName: string | null;
  level: number;
  path: string;
};

export type ApiCreateItemCategoryRequest = {
  code: string;
  name: string;
  parentId?: string | null;
};

export type ApiUpdateItemCategoryRequest = {
  name: string;
  parentId?: string | null;
};

/** MDM Item — mirrors BE `ItemDto`. */
export type ApiItem = {
  id: string;
  code: string;
  accountingCode?: string | null;
  name: string;
  description?: string | null;
  itemType: string;
  baseUomId: string;
  baseUomCode: string;
  lotTracked: boolean;
  shelfLifeDays: number | null;
  isRework: boolean;
  minStockQty: number | null;
  status: string;
  createdAt: string;
  categoryCode?: string | null;
};

export type ApiCreateItemRequest = {
  code: string;
  accountingCode?: string | null;
  name: string;
  description?: string | null;
  itemType: string;
  baseUomCode: string;
  lotTracked?: boolean;
  shelfLifeDays?: number | null;
  isRework?: boolean;
  minStockQty?: number | null;
};

export type ApiUpdateItemRequest = {
  accountingCode?: string | null;
  name: string;
  description?: string | null;
  itemType: string;
  lotTracked: boolean;
  shelfLifeDays: number | null;
  isRework: boolean;
  minStockQty: number | null;
  status: string;
};

export type ApiUom = {
  id: string;
  code: string;
  name: string;
  uomClass: string;
};

export type ApiCreateUomRequest = {
  code: string;
  name: string;
  uomClass: string;
};

export type ApiUpdateUomRequest = {
  name: string;
  uomClass: string;
};

export type ApiWarehouse = {
  id: string;
  plantId: string;
  code: string;
  name: string;
  whType: string;
  tempControlled: boolean;
  status: string;
  locationCount?: number;
};

export type ApiCreateWarehouseRequest = {
  plantCode: string;
  code: string;
  name: string;
  whType: string;
  tempControlled?: boolean;
};

export type ApiUpdateWarehouseRequest = {
  name: string;
  whType: string;
  tempControlled: boolean;
};

export type ApiSetWarehouseStatusRequest = {
  status: string;
};

export type ApiLocation = {
  id: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName?: string | null;
  code: string;
  name: string;
  locationType: string;
  status: string;
};

export type ApiCreateLocationRequest = {
  warehouseCode: string;
  code: string;
  name: string;
  locationType: string;
};

export type ApiUpdateLocationRequest = {
  name: string;
  locationType: string;
};

export type ApiSetLocationStatusRequest = {
  status: string;
};

export type ApiEquipmentSpecTemplateItem = {
  key: string;
  label: string;
  placeholder?: string | null;
  required?: boolean;
};

export type ApiEquipmentSpecItem = {
  id: string;
  key: string;
  label: string;
  value: string;
};

export type ApiEquipmentType = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: string;
  sortOrder: number;
  specTemplates: ApiEquipmentSpecTemplateItem[];
  equipmentCount?: number;
};

export type ApiCreateEquipmentTypeRequest = {
  code: string;
  name: string;
  description?: string | null;
  sortOrder?: number;
  specTemplates?: ApiEquipmentSpecTemplateItem[];
};

export type ApiUpdateEquipmentTypeRequest = {
  name: string;
  description?: string | null;
  sortOrder?: number;
  specTemplates?: ApiEquipmentSpecTemplateItem[];
};

export type ApiCloneEquipmentTypeRequest = {
  code: string;
  name: string;
  description?: string | null;
  sortOrder?: number;
  specTemplates?: ApiEquipmentSpecTemplateItem[];
};

export type ApiSetEquipmentTypeStatusRequest = {
  status: string;
};

export type ApiEquipment = {
  id: string;
  plantId: string;
  plantCode: string;
  code: string;
  name: string;
  equipmentType: string;
  lineId: string | null;
  lineCode: string | null;
  calibrationDueDate: string | null;
  status: string;
  description?: string | null;
  specifications?: ApiEquipmentSpecItem[] | null;
  attributes?: Record<string, string> | null;
};

export type ApiCreateEquipmentRequest = {
  plantCode: string;
  code: string;
  name: string;
  equipmentType: string;
  calibrationDueDate?: string | null;
  lineCode?: string | null;
  description?: string | null;
  specifications?: ApiEquipmentSpecItem[] | null;
  attributes?: Record<string, string> | null;
};

export type ApiUpdateEquipmentRequest = {
  name: string;
  equipmentType: string;
  calibrationDueDate?: string | null;
  lineCode?: string | null;
  description?: string | null;
  specifications?: ApiEquipmentSpecItem[] | null;
  attributes?: Record<string, string> | null;
};

export type ApiSetEquipmentStatusRequest = {
  status: string;
};

export type ApiEquipmentCalibration = {
  id: string;
  equipmentId: string;
  calibratedAt: string;
  dueDate: string;
  certificateNo: string | null;
  result: string | null;
  createdAt: string;
};

export type ApiCreateEquipmentCalibrationRequest = {
  calibratedAt: string;
  dueDate: string;
  certificateNo?: string | null;
  result?: string | null;
};

export type ApiDefectCode = {
  id: string;
  code: string;
  name: string;
  category: string;
  status: string;
};

export type ApiReasonCode = {
  id: string;
  code: string;
  name: string;
  category: string;
  status: string;
};

export type ApiCreateReasonCodeRequest = {
  code: string;
  name: string;
  category: string;
};

export type ApiSetReasonStatusRequest = {
  status: string;
};

export type ApiCreateDefectCodeRequest = {
  code: string;
  name: string;
  category: string;
};

export type ApiSetDefectStatusRequest = {
  status: string;
};

export type ApiUomConversion = {
  id: string;
  itemId: string | null;
  itemCode: string | null;
  fromUomCode: string;
  toUomCode: string;
  factor: number;
  createdAt?: string | null;
};

export type ApiCreateUomConversionRequest = {
  itemCode?: string | null;
  fromUomCode: string;
  toUomCode: string;
  factor: number;
};

/** Partner — mirrors BE `PartnerDto` (Customer / Supplier / both). */
export type ApiPartner = {
  id: string;
  code: string;
  name: string;
  partnerType: string;
  taxId: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  contactName: string | null;
  notes: string | null;
  status: string;
  creditLimit: number | null;
  outstandingBalance: number | null;
  hasOverdue: boolean;
  createdAt: string;
  industryCode?: string | null;
  customerSegmentCode?: string | null;
  debtSegmentCode?: string | null;
  debtDays?: number | null;
  allowOverdue?: boolean;
};

export type ApiPartnerStatusBucket = {
  status: string;
  count: number;
};

export type ApiPartnerListSummary = {
  activeCount: number;
  inactiveCount: number;
  totalCount: number;
  statusBuckets: ApiPartnerStatusBucket[];
};

export type ApiCreatePartnerRequest = {
  code: string;
  name: string;
  partnerType: string;
  taxId?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  contactName?: string | null;
  notes?: string | null;
  creditLimit?: number | null;
  outstandingBalance?: number | null;
  hasOverdue?: boolean;
  industryCode?: string | null;
  customerSegmentCode?: string | null;
  debtSegmentCode?: string | null;
  debtDays?: number | null;
  allowOverdue?: boolean;
};

export type ApiUpdatePartnerRequest = {
  name: string;
  partnerType: string;
  taxId: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  contactName: string | null;
  notes: string | null;
  creditLimit?: number | null;
  outstandingBalance?: number | null;
  hasOverdue?: boolean;
  industryCode?: string | null;
  customerSegmentCode?: string | null;
  debtSegmentCode?: string | null;
  debtDays?: number | null;
  allowOverdue?: boolean;
};

export type ApiSetPartnerStatusRequest = {
  status: string;
};

/** Phiếu đề xuất hạn mức công nợ (tín dụng + ngày được nợ) — mirrors BE `PartnerCreditLimitChangeDto`. */
export type ApiPartnerCreditLimitChange = {
  id: string;
  requestNo: string;
  partnerCode: string;
  partnerName: string;
  requesterUserId: string;
  requesterUsername: string;
  department: string;
  currentCreditLimit: number | null;
  proposedCreditLimit: number;
  currentDebtDays: number | null;
  proposedDebtDays: number;
  reason: string | null;
  status: string;
  approvalInstanceId: string | null;
  createdAt: string;
};

export type ApiCreatePartnerCreditLimitChangeRequest = {
  partnerCode: string;
  proposedCreditLimit: number;
  proposedDebtDays: number;
  reason?: string | null;
  department?: string | null;
};

export type ApiUpdatePartnerCreditLimitChangeRequest = {
  proposedCreditLimit: number;
  proposedDebtDays: number;
  reason?: string | null;
};

export type ApiSubmitPartnerCreditLimitChangeRequest = {
  department?: string | null;
};

/** Phiếu đề xuất đổi phân khúc đối tác + chính sách công nợ — mirrors BE `PartnerDebtSegmentChangeDto`. */
export type ApiPartnerDebtSegmentChange = {
  id: string;
  requestNo: string;
  partnerCode: string;
  partnerName: string;
  requesterUserId: string;
  requesterUsername: string;
  department: string;
  currentDebtSegmentCode: string | null;
  proposedDebtSegmentCode: string;
  currentDebtDays: number | null;
  proposedDebtDays: number;
  currentCreditLimit: number | null;
  proposedCreditLimit: number;
  currentAllowOverdue: boolean;
  proposedAllowOverdue: boolean;
  reason: string | null;
  status: string;
  approvalInstanceId: string | null;
  createdAt: string;
};

export type ApiCreatePartnerDebtSegmentChangeRequest = {
  partnerCode: string;
  proposedDebtSegmentCode: string;
  proposedDebtDays: number;
  proposedCreditLimit: number;
  proposedAllowOverdue?: boolean;
  reason?: string | null;
  department?: string | null;
};

export type ApiSubmitPartnerDebtSegmentChangeRequest = {
  department?: string | null;
};

export type ApiPartnerAddress = {
  id: string;
  partnerId: string;
  partnerType: string;
  addressTypeId: string;
  addressTypeCode: string;
  addressTypeName: string;
  address: string;
  contactName?: string | null;
  phone?: string | null;
  isVisible: boolean;
  isDefault: boolean;
};

export type ApiSavePartnerAddressRequest = {
  partnerType: string;
  addressTypeId: string;
  address: string;
  contactName?: string | null;
  phone?: string | null;
  isVisible?: boolean | null;
  isDefault?: boolean | null;
};

export type ApiPartnerSuppliedItem = {
  id: string;
  partnerId: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemType: string;
  categoryCode?: string | null;
  categoryName?: string | null;
  uomCode?: string | null;
  uomName?: string | null;
  status: string;
};

export type ApiAddPartnerSuppliedItemRequest = {
  itemCode: string;
};

/** SYS — mirrors BE PlantDto / UserListItemDto / RoleDto / AuditLogDto. */
export type ApiPlant = {
  id: string;
  code: string;
  name: string;
  status: string;
  timezone: string;
  createdAt: string;
};

export type ApiShift = {
  id: string;
  plantCode: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  status: string;
  createdAt: string;
};

export type ApiCreateShiftRequest = {
  plantCode: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
};

export type ApiSetShiftStatusRequest = {
  status: string;
};

/** SYS Category — mirrors BE `CategoryListItemDto` / `CategoryDto` / `CategoryDetailDto`. */
export type ApiCategoryListItem = {
  id: string;
  code: string;
  name: string;
  createdAt: string;
  updatedAt?: string | null;
};

export type ApiCategoryDetail = {
  id: string;
  code: string;
  name: string;
  value: string;
  description: string;
  orderNo: number;
  createdAt: string;
  updatedAt?: string | null;
};

export type ApiCategory = {
  id: string;
  code: string;
  name: string;
  createdAt: string;
  updatedAt?: string | null;
  details: ApiCategoryDetail[];
};

export type ApiCreateCategoryRequest = {
  code: string;
  name: string;
};

export type ApiUpdateCategoryRequest = {
  name: string;
};

export type ApiCreateCategoryDetailRequest = {
  code: string;
  name: string;
  value?: string | null;
  description?: string | null;
  orderNo?: number;
};

export type ApiUpdateCategoryDetailRequest = {
  name: string;
  value?: string | null;
  description?: string | null;
  orderNo?: number | null;
};

/** SYS mobile app release — mirrors BE `MobileAppReleaseDto`. */
export type ApiMobileAppPlatform = "Android" | "Ios";
export type ApiMobileAppReleaseStatus = "Draft" | "Published" | "Retired" | "Cancelled";

export type ApiMobileAppRelease = {
  id: string;
  platform: ApiMobileAppPlatform;
  versionName: string;
  releaseNotes: string;
  downloadUrl: string;
  forceUpdate: boolean;
  status: ApiMobileAppReleaseStatus;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
};

export type ApiUpsertMobileAppReleaseRequest = {
  platform: ApiMobileAppPlatform;
  versionName: string;
  releaseNotes?: string | null;
  downloadUrl?: string | null;
  forceUpdate: boolean;
};

export type ApiMobileAppUpdateCheck = {
  updateAvailable: boolean;
  forceUpdate: boolean;
  latestVersionName?: string | null;
  downloadUrl?: string | null;
  releaseNotes?: string | null;
};

export type ApiMobileAppPlatformSummary = {
  platform: ApiMobileAppPlatform;
  published: ApiMobileAppRelease | null;
  latest: ApiMobileAppRelease | null;
  draft: ApiMobileAppRelease | null;
  current: ApiMobileAppRelease | null;
  versionCount: number;
};

export type ApiNotificationRoleDefault = {
  eventType: string;
  roleCodes: string[];
  plantScoped: boolean;
};

export type ApiPermissionCatalogItem = {
  code: string;
  module: string;
  action: string;
  description?: string | null;
};

export type ApiCreatePlantRequest = {
  code: string;
  name: string;
  timezone?: string;
};

export type ApiCreateUserRequest = {
  username: string;
  fullName: string;
  password: string;
  defaultPlantId: string;
  roleCodes: string[];
  email?: string;
  department?: string;
};

export type ApiCreateRoleRequest = {
  code: string;
  name: string;
  scope: string;
};

/** BI Ops dashboards — mirrors BE Bi*DashboardDto (Sprint 7 P1). */
export type ApiBiLotHint = {
  lotCode: string;
  itemCode: string;
  expiryDate?: string | null;
  inventoryStatus: string;
  qcStatus: string;
};

export type ApiBiCodeCount = {
  code: string;
  count: number;
};

export type ApiBiStatusCount = {
  status: string;
  count: number;
};

export type ApiBiProdDashboard = {
  plantCode?: string | null;
  wipBatches: number;
  moReleased: number;
  moInProgress: number;
  moCompleted: number;
  openSoAtRisk: number;
  wipBatchHints: ApiBiCodeCount[];
};

export type ApiBiQcDashboard = {
  plantCode?: string | null;
  iqcPending: number;
  fqcPending: number;
  ipqcPending: number;
  openNcrs: number;
  openMesHolds: number;
  qcPendingLots: number;
  openNcrHints: ApiBiCodeCount[];
};

export type ApiBiWhDashboard = {
  plantCode?: string | null;
  expirySoonLots: number;
  minStockBreachCount: number;
  holdLots: number;
  quarantineLots: number;
  expiredLots: number;
  availableLots: number;
  lotsByInventoryStatus: ApiBiStatusCount[];
  expiryHints: ApiBiLotHint[];
};

/** EXE — mirrors BE Exe*Dto (Sprint 7). */
export type ApiExeKpi = {
  wipBatches: number;
  openHolds: number;
  qcPendingLots: number;
  fgReleasedLots: number;
  expirySoonLots: number;
  openNcrs: number;
  pendingApprovals: number;
  /** Hao hụt vượt ĐM (7 ngày) — L2 D-HH6. */
  overLossCount?: number;
};

export type ApiExeAlert = {
  severity: string;
  code: string;
  message: string;
  entityType?: string | null;
  entityId?: string | null;
};

export type ApiExeApproval = {
  id: string;
  requestNo: string;
  requestType: string;
  entityType: string;
  entityId: string;
  severity: string;
  summary: string;
  requesterId: string;
  status: string;
  dueAt?: string | null;
  rowVersion: number;
};

export type ApiExeReport = {
  code: string;
  title: string;
  summary: string;
  metrics: Record<string, number>;
};

export type ApiExeHome = {
  plantCode: string;
  kpis: ApiExeKpi;
  topAlerts: ApiExeAlert[];
  pendingApprovals: number;
};

export type ApiUserListItem = {
  id: string;
  username: string;
  fullName: string;
  status: string;
  defaultPlantId: string | null;
  defaultPlantCode: string | null;
  roleCodes: string[];
  createdAt: string;
};

export type ApiUserDetail = ApiUserListItem & {
  email: string | null;
  department: string | null;
  failedLoginCount: number;
  lockedAt: string | null;
};

export type ApiRole = {
  id: string;
  code: string;
  name: string;
  scope: string;
  permissions: string[];
  status?: string;
};

export type ApiResetPasswordResult = {
  temporaryPassword: string;
};

export type ApiAuditLog = {
  id: string;
  actorUserId: string | null;
  actorUsername?: string | null;
  entityType: string;
  entityId: string;
  action: string;
  createdAt: string;
  /** Compact snapshot JSON (FRS-SYS-04); null when writer omitted. */
  beforeJson?: string | null;
  afterJson?: string | null;
  ip?: string | null;
};

export type ApiEmailMessageLog = {
  id: string;
  to: string;
  subject: string;
  templateCode?: string | null;
  status: string;
  error?: string | null;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
  createdAt: string;
};

/** Optional filters for `GET /api/v1/sys/audit` (T4a entity-scoped). */
export type ApiAuditListFilters = {
  entityType?: string;
  entityId?: string;
};

/**
 * Frozen EntityType strings for AuditLog (W0 catalog).
 * Prefer these over ad-hoc literals when writing or filtering.
 */
export const AuditEntityTypes = {
  AppUser: "AppUser",
  Role: "Role",
  Plant: "Plant",
  ProductionLine: "ProductionLine",
  Organization: "Organization",
  SalesOrder: "SalesOrder",
  Shipment: "Shipment",
  PurchaseRequisition: "PurchaseRequisition",
  PurchaseOrder: "PurchaseOrder",
  Demand: "Demand",
  ProductionOrder: "ProductionOrder",
  Batch: "Batch",
  Lot: "Lot",
  InventoryTxn: "InventoryTxn",
  Inspection: "Inspection",
  QaRelease: "QaRelease",
  Ncr: "Ncr",
  Hold: "Hold",
  Bom: "Bom",
  Routing: "Routing",
  Spec: "Spec",
  QcPlan: "QcPlan",
  TechnicalInfo: "TechnicalInfo",
  Product: "Product",
  ProductType: "ProductType",
  OperationTemplate: "OperationTemplate",
  Partner: "Partner",
  FinReceipt: "FinReceipt",
  FinPayment: "FinPayment",
  Item: "Item",
  Equipment: "Equipment",
  Warehouse: "Warehouse",
  Location: "Location",
  ReasonCode: "ReasonCode",
  ApprovalPolicy: "ApprovalPolicy",
  ApprovalInstance: "ApprovalInstance",
  ApprovalRequest: "ApprovalRequest",
  NavGroup: "NavGroup",
  NavItem: "NavItem",
  NavProfile: "NavProfile",
  NotificationEventDefinition: "NotificationEventDefinition",
} as const;

export type AuditEntityType =
  (typeof AuditEntityTypes)[keyof typeof AuditEntityTypes];

/** Canonical Ops attachment entityType strings (BE `AttachedEntityTypes`). */
export const AttachedEntityTypes = {
  SalesOrder: "SalesOrder",
  PurchaseOrder: "PurchaseOrder",
  PurchaseRequisition: "PurchaseRequisition",
  Partner: "Partner",
  Item: "Item",
  Demand: "Demand",
  ProductionOrder: "ProductionOrder",
  Bom: "Bom",
  Routing: "Routing",
  Spec: "Spec",
  QcPlan: "QcPlan",
  Product: "Product",
  ProductType: "ProductType",
  OperationTemplate: "OperationTemplate",
  FormInstance: "FormInstance",
  Document: "Document",
  InventoryTxn: "InventoryTxn",
  Lot: "Lot",
  Ncr: "Ncr",
  Deviation: "Deviation",
} as const;

export type ApiAttachedEntityType =
  (typeof AttachedEntityTypes)[keyof typeof AttachedEntityTypes];

export {
  EnablementStatuses,
  EnablementStatusValues,
  EbrStatuses,
  ReservationStatuses,
  ReservationStatusValues,
  SalesOrderStatuses,
  SalesOrderStatusValues,
  ShipmentStatuses,
  ShipmentStatusValues,
  SalesFulfillmentTypes,
  SalesOrderPriorities,
  PurchaseRequisitionStatuses,
  PurchaseRequisitionStatusValues,
  PurchaseOrderStatuses,
  PurchaseOrderStatusValues,
  DemandStatuses,
  DemandStatusValues,
  DemandSourceTypes,
  ProductionOrderStatuses,
  ProductionOrderStatusValues,
  BatchSizingModes,
  ReadinessStatuses,
  BatchStatuses,
  BatchStatusValues,
  OperationExecutionStatuses,
  HoldStatuses,
  HoldStatusValues,
  HandoffStatuses,
  CipStatuses,
  ReworkOrderStatuses,
  ParamSpecStates,
  QcStatuses,
  QcStatusValues,
  InventoryStatuses,
  InventoryStatusValues,
  TxnStatuses,
  TxnStatusValues,
  TxnTypes,
  LotLinkTypes,
  CategoryCodes,
  InspectionTypes,
  InspectionResults,
  QaReleaseDecisions,
  NcrStatuses,
  NcrStatusValues,
  DeviationStatuses,
  DeviationStatusValues,
  VersionStatuses,
  VersionStatusValues,
  FormInstanceStatuses,
  ApprovalInstanceStatuses,
  ItemTypes,
  PartnerTypes,
  SeverityLevels,
  SeverityLevelValues,
  NotificationSeverities,
  type EnablementStatus,
  type EbrStatus,
  type ReservationStatus,
  type SalesOrderStatus,
  type ShipmentStatus,
  type SalesFulfillmentType,
  type SalesOrderPriority,
  type PurchaseRequisitionStatus,
  type PurchaseOrderStatus,
  type DemandStatus,
  type DemandSourceType,
  type ProductionOrderStatus,
  type BatchSizingMode,
  type ReadinessStatus,
  type BatchStatus,
  type OperationExecutionStatus,
  type HoldStatus,
  type HandoffStatus,
  type CipStatus,
  type ReworkOrderStatus,
  type ParamSpecState,
  type QcStatus,
  type InventoryStatus,
  type TxnStatus,
  type TxnType,
  type LotLinkType,
  type CategoryCode,
  type InspectionType,
  type InspectionResult,
  type QaReleaseDecision,
  type NcrStatus,
  type DeviationStatus,
  type VersionStatus,
  type FormInstanceStatus,
  type ApprovalInstanceStatus,
  type ItemType,
  type PartnerType,
  type SeverityLevel,
  type NotificationSeverity,
} from "./domainCodes";

export {
  PermissionCodes,
  PermissionCodeValues,
  RoleCodes,
  type PermissionCode,
  type RoleCode,
} from "./permissionCodes";

export {
  FinReceiptStatusValues,
  FinReceiptTypeValues,
  type ApiFinReceipt,
  type ApiFinReceiptListItem,
  type ApiFinReceiptConfirmation,
  type ApiFinReceiptListSummary,
  type ApiFinReceiptStatusBucket,
  type ApiCreateFinReceiptRequest,
  type ApiUpdateFinReceiptRequest,
  type ApiConfirmFinReceiptRequest,
  type ApiFinReceiptListQuery,
  type FinAllocationLineDto,
  type FinAllocationLineInput,
  type FinReceiptStatus,
  type FinReceiptType,
} from "./finReceipt";

export {
  FinPaymentStatusValues,
  FinPaymentTypeValues,
  type ApiFinPayment,
  type ApiFinPaymentListItem,
  type ApiFinPaymentConfirmation,
  type ApiFinPaymentListSummary,
  type ApiFinPaymentStatusBucket,
  type ApiCreateFinPaymentRequest,
  type ApiUpdateFinPaymentRequest,
  type ApiConfirmFinPaymentRequest,
  type ApiFinPaymentListQuery,
  type FinPaymentStatus,
  type FinPaymentType,
} from "./finPayment";

export type { ApiPartnerLedger } from "./finLedger";

export {
  FinTransactionTypeValues,
  FinObjectTypeValues,
  type ApiPartnerTransactionListItem,
  type ApiPartnerTransactionListQuery,
} from "./finTransaction";

export type {
  ApiPartnerDebtReportListItem,
  ApiPartnerDebtReportListResponse,
  ApiPartnerDebtOrderLine,
  ApiPartnerDebtUnallocatedDocument,
  ApiPartnerDebtReportDetail,
} from "./finDebtReport";

export type ApiEntityAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  objectKey: string;
  readUri: string | null;
  uploadedByName?: string | null;
};

export type ApiNotificationPreference = {
  eventType: string;
  channel: string;
  enabled: boolean;
};

export type ApiNotificationCatalogEvent = {
  eventType: string;
  groupCode: string;
  groupLabelVi: string;
  nameVi: string;
  description: string | null;
  sortOrder: number;
  isEnabled: boolean;
  updatedAt: string;
};

export type ApiInAppNotification = {
  id: string;
  eventType: string;
  title: string;
  body: string;
  severity: string;
  entityType: string | null;
  entityId: string | null;
  plantCode: string | null;
  routeHint: string | null;
  createdAt: string;
  readAt: string | null;
  isRead: boolean;
};

/** BE `PushDeviceDto` — registered browser/native push endpoint. */
export type ApiPushDevice = {
  id: string;
  platform: string;
  deviceToken: string;
  channelPref: string;
  status: string;
  lastSeenAt: string;
};

export type ApiRegisterPushDeviceRequest = {
  platform: string;
  deviceToken: string;
  channelPref?: string | null;
};

export type ApiOrderPackFormat = {
  code: string;
  label: string;
  netContent: number;
  netUomCode: string;
  countUomCode?: string | null;
  countUomName?: string | null;
};

export type ApiCustomerProductPack = {
  packFormat: string;
  label: string;
  netContent: number;
  netUomCode: string;
  fgItemCode: string;
  fgItemName: string;
  countUomCode?: string | null;
  countUomName?: string | null;
};

export type ApiCustomerProduct = {
  code: string;
  name: string;
  packs: ApiCustomerProductPack[];
};

/** SAL/PUR list rows — mirrors BE `*ListItemDto` (no lines; detail via Get). */
export type ApiSalesOrderListItem = {
  id: string;
  orderNo: string;
  plantCode: string;
  customerPartnerCode: string;
  customerName: string;
  fulfillment: SalesFulfillmentType | string;
  requestedDate: string | null;
  priority: string;
  promisedDate: string | null;
  status: SalesOrderStatus | string;
  notes: string | null;
  lineCount: number;
  totalAmount: number | null;
  createdAt: string;
  linesSummary: string;
  orderedQtyTotal: number;
  primaryUomCode: string | null;
  primaryUomName?: string | null;
  currency?: string;
  paymentMethod?: string | null;
  depositAmount?: number;
  remainingAmount?: number | null;
};

export type ApiSalesOrderStatusBucket = {
  status: SalesOrderStatus | string;
  count: number;
};

export type ApiSalesOrderListSummary = {
  openCount: number;
  overdueCount: number;
  atRiskCount: number;
  inProductionCount: number;
  readyCount: number;
  completedCount: number;
  statusBuckets: ApiSalesOrderStatusBucket[];
};

export type ApiSalesOrderLineDemand = {
  demandNo: string;
  status: string;
  qty: number;
};

export type ApiSalesOrderLineMo = {
  orderNo: string;
  status: string;
  plannedQty: number;
};

export type ApiSalesOrderLineProduction = {
  demandNo: string | null;
  demandStatus: string | null;
  productionOrders: ApiSalesOrderLineMo[];
  goodQtyTotal: number;
  plannedQtyTotal: number;
  productionSummaryCode: string;
  /** EXACT preview from Effective BOM baseBatch; null when missing. */
  estimatedBatchCount?: number | null;
};

export type ApiSalesOrderLine = {
  id: string;
  lineNo: number;
  itemCode: string;
  itemName: string;
  qty: number;
  uomCode: string;
  uomName?: string | null;
  lineType: string;
  unitPrice: number | null;
  lineAmount: number | null;
  vatPercent?: number;
  discountAmount?: number;
  vatTaxRateCode?: string | null;
  packFormat: string | null;
  packFormatLabel?: string | null;
  packQty?: number | null;
  packUomCode?: string | null;
  packUomName?: string | null;
  demands: ApiSalesOrderLineDemand[];
  production?: ApiSalesOrderLineProduction | null;
};

export type ApiSalesOrderJourneyStep = {
  id: string;
  label: string;
  state: string;
};

export type ApiSalesOrderTimelineEvent = {
  id: string;
  at: string;
  source: string;
  action: string;
  label: string;
  detail: string | null;
};

export type ApiSalesOrder = {
  id: string;
  orderNo: string;
  plantCode: string;
  customerPartnerCode: string;
  customerName: string;
  customerTaxId: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  customerAddress: string | null;
  customerContactName: string | null;
  customerStatus: string;
  fulfillment: SalesFulfillmentType | string;
  requestedDate: string | null;
  priority: string;
  promisedDate: string | null;
  shipToAddress: string | null;
  salesOwnerUserId: string | null;
  salesOwnerName: string | null;
  daysUntilDue: number | null;
  isOverdue: boolean;
  isSoonDue: boolean;
  status: SalesOrderStatus | string;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  lineCount: number;
  totalAmount: number | null;
  vatPercent?: number;
  discountAmount?: number;
  goodsAmount?: number | null;
  vatAmount?: number | null;
  currency?: string;
  paymentMethod?: string | null;
  depositAmount?: number;
  remainingAmount?: number | null;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
  lines: ApiSalesOrderLine[];
  journey?: ApiSalesOrderJourneyStep[] | null;
  timeline?: ApiSalesOrderTimelineEvent[] | null;
  warnings?: string[] | null;
  /** FIN-13 — cọc cần xử lý sau hủy (chưa tự hoàn). */
  depositPendingHandling?: number | null;
};

export type ApiSalesOrderLineInput = {
  itemCode: string;
  qty: number;
  uomCode: string;
  lineType?: string;
  unitPrice?: number | null;
  packFormat?: string | null;
  vatPercent?: number;
  discountAmount?: number;
  vatTaxRateCode?: string | null;
  packQty?: number | null;
  packUomCode?: string | null;
};

export type ApiCreateSalesOrderRequest = {
  plantCode: string;
  customerPartnerCode: string;
  fulfillment: string;
  requestedDate?: string | null;
  notes?: string | null;
  lines: ApiSalesOrderLineInput[];
  priority?: string;
  promisedDate?: string | null;
  shipToAddress?: string | null;
  salesOwnerUserId?: string | null;
  vatPercent?: number;
  discountAmount?: number;
  currency?: string;
  paymentMethod?: string | null;
  depositAmount?: number;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiUpdateSalesOrderRequest = {
  requestedDate: string | null;
  notes: string | null;
  lines: ApiSalesOrderLineInput[];
  priority?: string;
  promisedDate?: string | null;
  shipToAddress?: string | null;
  salesOwnerUserId?: string | null;
  vatPercent?: number;
  discountAmount?: number;
  currency?: string;
  paymentMethod?: string | null;
  depositAmount?: number;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

/** Result of POST …/sales-orders/{orderNo}/lines/{lineId}/demands */
export type ApiCreateDemandFromSoLineResult = {
  demandId: string;
  demandNo: string;
  salesOrderStatus: string;
};

/** GET …/sales-orders/{orderNo}/ship-balance */
export type ApiSoLineShipBalance = {
  salesOrderLineId: string;
  lineNo: number;
  itemCode: string;
  itemName: string;
  orderedQty: number;
  shippedQty: number;
  remainingQty: number;
  uomCode: string;
  uomName?: string | null;
  packQty?: number | null;
  packFormat?: string | null;
  packUomCode?: string | null;
  packUomName?: string | null;
};

export type ApiShipmentLineInput = {
  salesOrderLineId: string;
  qty: number;
  lotCode?: string | null;
};

export type ApiCreateShipmentFromOrderRequest = {
  shipDate?: string | null;
  deliveryAddress?: string | null;
  notes?: string | null;
  lines?: ApiShipmentLineInput[] | null;
  customerName?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
};

export type ApiUpdateShipmentDraftRequest = {
  shipDate?: string | null;
  deliveryAddress?: string | null;
  notes?: string | null;
  lines?: ApiShipmentLineInput[] | null;
  customerName?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
};

export type ApiShipmentLine = {
  id: string;
  lineNo: number;
  salesOrderLineId: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uomCode: string;
  lotCode: string | null;
  uomName?: string | null;
  orderedQty?: number | null;
  packQty?: number | null;
  packUomCode?: string | null;
  packUomName?: string | null;
};

export type ApiShipment = {
  id: string;
  shipmentNo: string;
  salesOrderNo: string;
  plantCode: string;
  customerPartnerCode: string;
  customerName: string;
  customerTaxId: string | null;
  customerPhone: string | null;
  customerAddress: string | null;
  contactName?: string | null;
  status: ShipmentStatus | string;
  shipDate: string | null;
  deliveryAddress: string | null;
  notes: string | null;
  createdAt: string;
  lines: ApiShipmentLine[];
};

export type ApiShipmentListItem = {
  id: string;
  shipmentNo: string;
  salesOrderNo: string;
  customerPartnerCode?: string;
  customerName?: string;
  status: string;
  shipDate: string | null;
  createdAt?: string | null;
  lineCount: number;
  qtyTotal: number;
  uomCode?: string | null;
  uomName?: string | null;
  /** SL đặt — vd. "100 Xô". */
  orderedQtyLabel?: string | null;
  /** SL giao & đóng gói — vd. "90 Xô ~ 9 Thùng". */
  shipAndPackLabel?: string | null;
  /** Quy đổi ĐVT — vd. "(10 xô = 1 thùng)". */
  packConversionHint?: string | null;
};

export type ApiPurchaseRequisitionListItem = {
  id: string;
  requisitionNo: string;
  plantCode: string;
  requesterUserId: string;
  requesterUsername: string;
  department: string;
  status: PurchaseRequisitionStatus | string;
  notes: string | null;
  lineCount: number;
  createdAt: string;
};

export type ApiPurchaseRequisitionStatusBucket = {
  status: string;
  count: number;
};

export type ApiPurchaseRequisitionListSummary = {
  draftCount: number;
  submittedCount: number;
  approvedCount: number;
  convertedCount: number;
  statusBuckets: ApiPurchaseRequisitionStatusBucket[];
};

export type ApiPurchaseRequisitionLine = {
  id: string;
  lineNo: number;
  itemCode: string;
  qty: number;
  uomCode: string;
  neededDate: string | null;
  supplierPartnerCode?: string | null;
  supplierName?: string | null;
  baseUomCode?: string;
  conversionFactor?: number | null;
  notes?: string | null;
  unitPrice?: number | null;
  lineAmount?: number | null;
  vatPercent?: number;
  discountAmount?: number;
  vatTaxRateCode?: string | null;
};

export type ApiPurchaseRequisition = {
  id: string;
  requisitionNo: string;
  plantCode: string;
  requesterUserId: string;
  requesterUsername: string;
  department: string;
  status: string;
  notes: string | null;
  lines: ApiPurchaseRequisitionLine[];
  vatPercent?: number;
  discountAmount?: number;
  currency?: string;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiPurchaseRequisitionLineInput = {
  itemCode: string;
  qty: number;
  uomCode: string;
  neededDate?: string | null;
  supplierPartnerCode?: string | null;
  conversionFactor?: number | null;
  notes?: string | null;
  unitPrice?: number | null;
  lineAmount?: number | null;
  vatPercent?: number;
  discountAmount?: number;
  vatTaxRateCode?: string | null;
};

export type ApiCreatePurchaseRequisitionRequest = {
  plantCode: string;
  department: string;
  notes?: string | null;
  lines: ApiPurchaseRequisitionLineInput[];
  vatPercent?: number;
  discountAmount?: number;
  currency?: string;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiUpdatePurchaseRequisitionRequest = {
  department: string;
  notes?: string | null;
  lines: ApiPurchaseRequisitionLineInput[];
  vatPercent?: number;
  discountAmount?: number;
  currency?: string;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiApproveStepRequest = {
  comment?: string | null;
};

export type ApiConvertPrToPoLineInput = {
  lineId: string;
  supplierPartnerCode: string;
  expectedDate?: string | null;
  notes?: string | null;
};

export type ApiConvertPrToPoRequest = {
  lines: ApiConvertPrToPoLineInput[];
  currency?: string | null;
  deliveryAddress?: string | null;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiPurchaseOrderListItem = {
  id: string;
  orderNo: string;
  plantCode: string;
  supplierPartnerCode: string;
  supplierName: string;
  sourceRequisitionNo: string | null;
  expectedDate: string | null;
  status: PurchaseOrderStatus | string;
  notes: string | null;
  lineCount: number;
  createdAt: string;
  linesSummary: string;
  orderedQtyTotal: number;
  receivedQtyTotal: number;
  remainingQtyTotal: number;
  currency: string;
  grandTotal: number | null;
};

export type ApiPurchaseOrderStatusBucket = {
  status: string;
  count: number;
};

export type ApiPurchaseOrderListSummary = {
  openCount: number;
  overdueCount: number;
  partialCount: number;
  receivedCount: number;
  statusBuckets: ApiPurchaseOrderStatusBucket[];
};

export type ApiPurchaseOrderLine = {
  id: string;
  lineNo: number;
  itemCode: string;
  itemName: string;
  orderedQty: number;
  receivedQty: number;
  remainingQty: number;
  uomCode: string;
  uomName?: string | null;
  unitPrice?: number | null;
  lineAmount?: number | null;
  vatPercent?: number;
  discountAmount?: number;
  vatTaxRateCode?: string | null;
  baseUomCode?: string | null;
  conversionFactor?: number | null;
  notes?: string | null;
};

export type ApiPurchaseOrder = {
  id: string;
  orderNo: string;
  plantCode: string;
  supplierPartnerCode: string;
  supplierName: string;
  supplierTaxId: string | null;
  supplierPhone: string | null;
  supplierEmail: string | null;
  supplierAddress: string | null;
  supplierContactName: string | null;
  supplierStatus: string;
  sourceRequisitionNo: string | null;
  expectedDate: string | null;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  lineCount: number;
  orderedQtyTotal: number;
  receivedQtyTotal: number;
  remainingQtyTotal: number;
  lines: ApiPurchaseOrderLine[];
  currency?: string;
  vatRate?: number | null;
  deliveryAddress?: string | null;
  paymentTerms?: string | null;
  subtotal?: number | null;
  vatAmount?: number | null;
  grandTotal?: number | null;
  vatPercent?: number;
  discountAmount?: number;
  paymentMethod?: string | null;
  depositAmount?: number;
  remainingAmount?: number | null;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
  warnings?: string[] | null;
  /** FIN-13 — cọc cần xử lý sau hủy (chưa tự hoàn). */
  depositPendingHandling?: number | null;
};

export type ApiConvertPrToPoResult = {
  orders: ApiPurchaseOrder[];
};

export type ApiPurchaseOrderLineInput = {
  itemCode: string;
  orderedQty: number;
  uomCode: string;
  unitPrice?: number | null;
  vatPercent?: number;
  discountAmount?: number;
  vatTaxRateCode?: string | null;
  conversionFactor?: number | null;
  notes?: string | null;
};

export type ApiCreatePurchaseOrderRequest = {
  plantCode: string;
  supplierPartnerCode: string;
  expectedDate?: string | null;
  notes?: string | null;
  lines: ApiPurchaseOrderLineInput[];
  currency?: string;
  vatRate?: number | null;
  deliveryAddress?: string | null;
  paymentTerms?: string | null;
  vatPercent?: number;
  discountAmount?: number;
  paymentMethod?: string | null;
  depositAmount?: number;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiUpdatePurchaseOrderRequest = {
  expectedDate: string | null;
  notes: string | null;
  lines: ApiPurchaseOrderLineInput[];
  currency?: string | null;
  vatRate?: number | null;
  deliveryAddress?: string | null;
  paymentTerms?: string | null;
  vatPercent?: number;
  discountAmount?: number;
  paymentMethod?: string | null;
  depositAmount?: number;
  exchangeRate?: number | null;
  exchangeRateDate?: string | null;
};

export type ApiPreparePoReceiptLineInput = {
  lineId: string;
  qty: number;
  supplierLotCode?: string | null;
  locationCode?: string | null;
  mfgDate?: string | null;
  expiryDate?: string | null;
};

export type ApiPreparePoReceiptRequest = {
  lines: ApiPreparePoReceiptLineInput[];
  locationCode?: string | null;
  autoPostGr?: boolean;
};

export type ApiPreparePoReceipt = {
  orderNo: string;
  orderStatus: string;
  txnNo: string;
  txnStatus: string;
  lotCodes: string[];
  lines: ApiPurchaseOrderLine[];
};

export type ApiWmsTxnLine = {
  id: string;
  lineNo: number;
  itemCode: string;
  itemName?: string | null;
  lotCode: string;
  locationCode: string;
  toLocationCode?: string | null;
  qty: number;
  uomCode: string;
  uomName?: string | null;
  baseQty: number;
  conversionFactor: number;
  baseUomCode?: string | null;
  /** Lot expiry (YYYY-MM-DD) when available. */
  expiryDate?: string | null;
};

export type ApiWmsTxn = {
  id: string;
  txnNo: string;
  txnType: string;
  status: TxnStatus | string;
  txnDate: string;
  plantCode: string;
  clientRequestId?: string | null;
  notes?: string | null;
  lines: ApiWmsTxnLine[];
  partnerCode?: string | null;
  partnerName?: string | null;
  fromWarehouseCode?: string | null;
  fromWarehouseName?: string | null;
  toWarehouseCode?: string | null;
  toWarehouseName?: string | null;
  postedAt?: string | null;
  postedBy?: string | null;
  /** Linked document type (e.g. Batch). */
  refType?: string | null;
  /** Linked document key (e.g. batch code). */
  refId?: string | null;
  createdAt?: string | null;
};

export type ApiWmsTxnStatusBucket = {
  status: string;
  count: number;
};

export type ApiWmsTxnListSummary = {
  draftCount: number;
  postedCount: number;
  voidCount: number;
  totalCount: number;
  statusBuckets: ApiWmsTxnStatusBucket[];
};

export type ApiWmsLotInventoryBucket = {
  inventoryStatus: string;
  count: number;
};

export type ApiWmsLotQcBucket = {
  qcStatus: string;
  count: number;
};

export type ApiWmsLotListSummary = {
  availableCount: number;
  quarantineCount: number;
  holdCount: number;
  qcPendingCount: number;
  inventoryBuckets: ApiWmsLotInventoryBucket[];
  qcBuckets: ApiWmsLotQcBucket[];
};

export type ApiWmsLot = {
  id: string;
  lotCode: string;
  itemCode: string;
  plantId: string;
  plantCode: string;
  mfgDate: string | null;
  expiryDate: string | null;
  qcStatus: QcStatus | string;
  inventoryStatus: InventoryStatus | string;
  supplierCode: string | null;
  sourceType?: string;
  sourceRef?: string | null;
  receivedAt?: string | null;
  parentLotCode?: string | null;
  createdAt?: string | null;
};

export type ApiQmsInspectionResultLine = {
  checkCode: string;
  resultTri: string;
  valueText?: string | null;
  valueNumber?: number | null;
  correctiveAction?: string | null;
  seq?: number;
  prompt?: string | null;
  isRequired?: boolean;
  answerType?: string | null;
  defectOnFail?: boolean;
  minValue?: number | null;
  maxValue?: number | null;
  step?: number | null;
  uomLabel?: string | null;
  optionsJson?: string | null;
};

export type ApiQmsInspection = {
  id: string;
  inspectionNo: string;
  inspectionType: string;
  plantCode: string;
  lotCode: string | null;
  batchCode: string | null;
  result: string;
  inspectorId: string | null;
  inspectedAt: string | null;
  notes: string | null;
  results: ApiQmsInspectionResultLine[];
  qcPlanCode?: string | null;
  qcPlanName?: string | null;
  linkedNcrNo?: string | null;
};

export type ApiCreateInspectionRequest = {
  plantCode: string;
  inspectionType: string;
  lotCode?: string | null;
  batchCode?: string | null;
  notes?: string | null;
  clientRequestId?: string | null;
  results?: ApiQmsInspectionResultLine[] | null;
};

export type ApiCompleteInspectionRequest = {
  result: string;
  results?: ApiQmsInspectionResultLine[] | null;
  notes?: string | null;
  autoCreateNcrOnFail?: boolean;
};

export type ApiQaRelease = {
  id: string;
  releaseNo: string;
  lotCode: string;
  decision: string;
  prevQcStatus: string;
  prevInventoryStatus: string;
  newQcStatus: string;
  newInventoryStatus: string;
  decidedBy: string;
  decidedAt: string;
  notes: string | null;
  itemCode?: string | null;
  inspectionId?: string | null;
};

export type ApiQaReleaseCandidate = {
  lotCode: string;
  itemCode: string;
  plantCode: string;
  qcStatus: string;
  inventoryStatus: string;
  supplierCode: string | null;
  hasOpenNcr: boolean;
  openNcrNos: string[];
};

export type ApiCreateQaReleaseRequest = {
  lotCode: string;
  decision: string;
  inspectionId?: string | null;
  notes?: string | null;
};

export type ApiQmsNcrDisposition = {
  id: string;
  disposition: string;
  approvedBy: string | null;
  approvedByName?: string | null;
  approvedAt: string | null;
  notes: string | null;
};

export type ApiQmsNcr = {
  id: string;
  ncrNo: string;
  sourceType: string;
  lotCode: string | null;
  batchCode: string | null;
  severity: string;
  status: string;
  description: string;
  dispositions: ApiQmsNcrDisposition[];
};

export type ApiCreateNcrRequest = {
  description: string;
  severity?: string;
  sourceType?: string;
  lotCode?: string | null;
  batchCode?: string | null;
  inspectionId?: string | null;
  placeLotOnHold?: boolean;
};

export type ApiAddNcrDispositionRequest = {
  disposition: string;
  notes?: string | null;
  closeNcr?: boolean;
};

/** PLN list rows */
export type ApiDemandRelatedMo = {
  orderNo: string;
  status: string;
  plannedQty: number;
  uomCode: string;
  batchCount: number;
};

export type ApiDemand = {
  id: string;
  demandNo: string;
  plantCode: string;
  productCode: string;
  packFormat: string;
  qty: number;
  uomCode: string;
  uomName?: string | null;
  packFormatLabel?: string | null;
  productName?: string | null;
  dueDate: string | null;
  status: DemandStatus | string;
  notes?: string | null;
  sourceType?: string | null;
  salesOrderLineId?: string | null;
  salesOrderNo?: string | null;
  customerPartnerCode?: string | null;
  customerName?: string | null;
  relatedOrders?: ApiDemandRelatedMo[] | null;
  salesOrderQty?: number | null;
  createdByUsername?: string | null;
};

export type ApiDemandStatusBucket = {
  status: string;
  count: number;
};

export type ApiDemandListSummary = {
  openCount: number;
  plannedCount: number;
  closedCount: number;
  cancelledCount: number;
  statusBuckets: ApiDemandStatusBucket[];
};

export type ApiCreateDemandRequest = {
  plantCode: string;
  productCode: string;
  packFormat: string;
  qty: number;
  uomCode?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  salesOrderLineId?: string | null;
};

export type ApiUpdateDemandRequest = {
  productCode: string;
  packFormat: string;
  qty: number;
  uomCode?: string | null;
  dueDate?: string | null;
  notes?: string | null;
};

export type ApiProductionOrderListItem = {
  id: string;
  orderNo: string;
  orderType: string;
  plantCode: string;
  productCode: string;
  packFormat: string;
  bomCode: string;
  bomVersion: number;
  routingCode: string;
  routingVersion: number;
  plannedQty: number;
  uomCode: string;
  status: ProductionOrderStatus | string;
  demandNo: string | null;
  salesOrderNo?: string | null;
  customerName?: string | null;
  productName?: string | null;
  packFormatLabel?: string | null;
};

export type ApiProductionOrderStatusBucket = {
  status: string;
  count: number;
};

export type ApiProductionOrderListSummary = {
  draftCount: number;
  releasedCount: number;
  inProgressCount: number;
  completedCount: number;
  statusBuckets: ApiProductionOrderStatusBucket[];
};

export type ApiMaterialRequirement = {
  itemCode: string;
  qtyRequired: number;
  uomCode: string;
  baseQty: number;
  itemName?: string | null;
  status?: string | null;
  /** From BOM line → routing op; null = general MO requirement. */
  operationCode?: string | null;
  operationName?: string | null;
  operationSeq?: number | null;
};

export type ApiReservationLink = {
  id: string;
  itemCode: string;
  lotCode: string | null;
  qtyReserved: number;
  uomCode: string;
  status: string;
  stockReservationId: string | null;
};

export type ApiBatchPlanLine = {
  seq: number;
  plannedQty: number;
  uomCode: string;
};

export type ApiProductionOrder = ApiProductionOrderListItem & {
  materials: ApiMaterialRequirement[];
  reservations: ApiReservationLink[];
  frozenSpecCode?: string | null;
  frozenSpecVersion?: number | null;
  frozenQcPlans?: { code: string; versionNo: number }[] | null;
  /** Demand/SO qty — unchanged on FULL_BATCHES. */
  orderQty?: number;
  batchSizingMode?: "EXACT" | "FULL_BATCHES" | "MANUAL" | string;
  surplusQty?: number;
  surplusDisposition?: string | null;
  batchPlan?: ApiBatchPlanLine[] | null;
  technicalInfoCode?: string | null;
  technicalInfoVersion?: number | null;
  technicalInfoStatus?: string | null;
  productName?: string | null;
  packFormatLabel?: string | null;
  uomName?: string | null;
};

/** Propose batch plan (pure math DTO; wire in BS3). */
export type ApiProposedBatchPlan = {
  batches: ApiBatchPlanLine[];
  productionQty: number;
  surplusQty: number;
  fallback: boolean;
  warning?: string | null;
};

export type ApiLinkReservationRequest = {
  lotCode: string;
  locationCode: string;
  qty: number;
  uomCode: string;
};

export type ApiFefoSuggestion = {
  lotCode: string;
  locationCode: string;
  expiryDate: string | null;
  qtyAvailable: number;
  uomCode: string;
};

export type ApiMoMaterialFefo = {
  itemCode: string;
  qtyRequired: number;
  uomCode: string;
  baseQty: number;
  qtyAlreadyReserved: number;
  qtyStillNeeded: number;
  qtyCovered: number;
  shortage: boolean;
  suggestions: ApiFefoSuggestion[];
};

export type ApiFefoSuggestResult = {
  lines: ApiFefoSuggestion[];
  qtyRequested: number;
  qtyCovered: number;
  shortage: boolean;
};

export type ApiFefoRequest = {
  itemCode: string;
  qtyBase: number;
  warehouseCode?: string | null;
  allowPartial?: boolean;
};

export type ApiBalance = {
  id: string;
  lotCode: string;
  itemCode: string;
  locationCode: string;
  warehouseCode: string;
  qtyOnHand: number;
  qtyAvailable: number;
  qtyReserved: number;
  qtyHold: number;
  uomCode: string;
  inventoryStatus: string;
  qcStatus: string;
  minStockQty?: number | null;
  /** ĐVT nhập từ phiếu GR tạo lô. */
  packUomCode?: string | null;
  packUomName?: string | null;
  conversionFactor?: number | null;
};

export type ApiWmsStockSummary = {
  itemCount: number;
  balanceLineCount: number;
  totalOnHand: number;
  totalAvailable: number;
  totalReserved: number;
  totalHold: number;
  minStockBreachCount: number;
};

export type ApiStockReportQuery = {
  warehouseCode?: string;
  itemCode?: string;
  fromDate?: string;
  toDate?: string;
  month?: string;
  q?: string;
  page?: number;
  pageSize?: number;
};

export type ApiStockReportItem = {
  itemCode: string;
  accountingCode?: string | null;
  itemName: string;
  itemType?: string | null;
  warehouseCode: string;
  warehouseName: string;
  uomCode: string;
  openingQty: number;
  inwardQty: number;
  outwardQty: number;
  closingQty: number;
  availableQty: number;
  reservedQty: number;
  minStockQty?: number | null;
  minStockBreach: boolean;
};

export type ApiStockReportSummary = {
  totalItems: number;
  totalOpeningQty: number;
  totalInwardQty: number;
  totalOutwardQty: number;
  totalClosingQty: number;
  minStockBreachCount: number;
};

export type ApiStockReportResponse = {
  items: ApiPagedResult<ApiStockReportItem>;
  summary: ApiStockReportSummary;
};

export type ApiWmsExpiryLot = {
  id: string;
  lotCode: string;
  itemCode: string;
  plantCode: string;
  expiryDate: string | null;
  daysToExpiry: number | null;
  bucket: string;
  qcStatus: string;
  inventoryStatus: string;
  supplierCode: string | null;
};

export type ApiWmsExpirySummary = {
  expiredCount: number;
  soonCount: number;
  trackedCount: number;
  withinDays: number;
};

export type ApiWmsCountLine = {
  id: string;
  lineNo: number;
  itemCode: string;
  lotCode: string;
  locationCode: string;
  systemQty: number;
  countedQty: number;
  varianceQty: number;
  uomCode: string;
  varianceReason: string | null;
};

export type ApiWmsCount = {
  id: string;
  countNo: string;
  status: string;
  countDate: string;
  plantCode: string;
  clientRequestId: string | null;
  notes: string | null;
  createdBy: string | null;
  warehouseCode?: string | null;
  warehouseName?: string | null;
  lines: ApiWmsCountLine[];
};

export type ApiWmsCountListSummary = {
  draftCount: number;
  pendingApprovalCount: number;
  postedCount: number;
  totalCount: number;
  statusBuckets: Array<{ status: string; count: number }>;
};

export type ApiWmsDisposalLine = {
  id: string;
  lineNo: number;
  itemCode: string;
  lotCode: string;
  locationCode: string;
  qty: number;
  uomCode: string;
};

export type ApiWmsDisposal = {
  id: string;
  disposalNo: string;
  status: string;
  disposalDate: string;
  plantCode: string;
  clientRequestId: string | null;
  notes: string | null;
  reasonCode: string;
  ncrId: string | null;
  ncrNo: string | null;
  evidenceCount: number;
  warehouseCode?: string | null;
  warehouseName?: string | null;
  lines: ApiWmsDisposalLine[];
};

export type ApiCreateProductionOrderRequest = {
  plantCode: string;
  productCode: string;
  packFormat: string;
  plannedQty: number;
  bomCode?: string | null;
  bomVersion?: number | null;
  routingCode?: string | null;
  routingVersion?: number | null;
  technicalInfoCode?: string | null;
  technicalInfoVersion?: number | null;
  orderType?: string;
  demandId?: string | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  lineCode?: string | null;
  batchSizingMode?: "EXACT" | "FULL_BATCHES" | "MANUAL" | string | null;
  batchPlan?: ApiBatchPlanLine[] | null;
};

export type ApiUpdateProductionOrderRequest = {
  productCode: string;
  packFormat: string;
  plannedQty: number;
  technicalInfoCode?: string | null;
  technicalInfoVersion?: number | null;
  orderType?: string | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  lineCode?: string | null;
  batchSizingMode?: "EXACT" | "FULL_BATCHES" | "MANUAL" | string | null;
  batchPlan?: ApiBatchPlanLine[] | null;
};

export type ApiUpdateBatchPlanRequest = {
  batchSizingMode?: "EXACT" | "FULL_BATCHES" | "MANUAL" | string | null;
  batchPlan?: ApiBatchPlanLine[] | null;
};

export type ApiReadinessItem = {
  checkCode: string;
  message: string;
  isOk: boolean;
};

export type ApiReadiness = {
  id: string;
  orderNo: string;
  status: string;
  checkedAt: string | null;
  items: ApiReadinessItem[];
};

/** ENG Product + pack */
export type ApiProductListItem = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  status: EnablementStatus | string;
  packCount: number;
  customerPartnerCode?: string | null;
  customerName?: string | null;
  productTypeCode?: string | null;
  productTypeName?: string | null;
  createdAt?: string | null;
};

export type ApiProductPack = {
  id: string;
  productId: string;
  productCode: string;
  packFormat: string;
  packFormatLabel?: string | null;
  netContent: number;
  netUomCode: string;
  fgItemCode: string;
  fgItemName?: string | null;
  shelfLifeDays: number | null;
  status: string;
};

export type ApiProductRelatedVersion = {
  code: string;
  versionNo: number;
  status: string;
  packFormat: string;
};

export type ApiProductDetail = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  status: string;
  packs: ApiProductPack[];
  boms: ApiProductRelatedVersion[];
  routings: ApiProductRelatedVersion[];
  specs: ApiProductRelatedVersion[];
  customerPartnerCode?: string | null;
  customerName?: string | null;
  productTypeCode?: string | null;
  productTypeName?: string | null;
};

export type ApiProduct = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  status: string;
  customerPartnerCode?: string | null;
  customerName?: string | null;
  productTypeCode?: string | null;
  productTypeName?: string | null;
};

export type ApiCreateProductRequest = {
  code: string;
  name: string;
  customerCode: string;
  productTypeCode: string;
  description?: string | null;
};

export type ApiUpdateProductRequest = {
  name: string;
  customerCode: string;
  productTypeCode: string;
  description?: string | null;
};

export type ApiOperationEquipment = {
  code: string;
  name: string;
  plantCode: string;
};

/** Checklist gắn công đoạn theo mốc (PreStart / PreComplete). */
export type ApiOperationChecklistAttachment = {
  documentMasterCode: string | null;
  documentRequired: boolean;
  qcPlanId: string | null;
  qcPlanCode: string | null;
  qcRequired: boolean;
};

export type ApiOperationChecklistPhases = {
  preStart: ApiOperationChecklistAttachment;
  preComplete: ApiOperationChecklistAttachment;
};

export type ApiProductTypeOperation = {
  id: string;
  seq: number;
  operationCode: string;
  name: string;
  templateCode: string | null;
  equipment?: ApiOperationEquipment[] | null;
  isHoldPoint?: boolean;
  qcPlanCode?: string | null;
  documentMasterCode?: string | null;
  checklistPhases?: ApiOperationChecklistPhases | null;
};

export type ApiProductTypeMaterial = {
  id: string;
  lineNo: number;
  itemCode: string;
  itemName: string;
  qty: number;
  uomCode: string;
  allowReworkInput: boolean;
  scrapPct: number;
  operationSeq: number | null;
  operationCode: string | null;
};

export type ApiProductType = {
  id: string;
  code: string;
  name: string;
  status: string;
  operations: ApiProductTypeOperation[];
  materials: ApiProductTypeMaterial[];
};

export type ApiProductTypeListItem = {
  id: string;
  code: string;
  name: string;
  status: string;
  operationCount: number;
  materialCount: number;
};

export type ApiOperationChecklistAttachmentInput = {
  documentMasterCode?: string | null;
  documentRequired?: boolean;
  qcPlanCode?: string | null;
  qcRequired?: boolean;
};

export type ApiOperationChecklistPhasesInput = {
  preStart?: ApiOperationChecklistAttachmentInput | null;
  preComplete?: ApiOperationChecklistAttachmentInput | null;
};

export type ApiProductTypeOperationInput = {
  operationTemplateCode: string;
  equipment?: Array<{ code: string; plantCode?: string | null }> | null;
  isHoldPoint?: boolean;
  qcPlanCode?: string | null;
  checklistPhases?: ApiOperationChecklistPhasesInput | null;
};

export type ApiProductTypeMaterialInput = {
  itemCode: string;
  qty: number;
  uomCode: string;
  allowReworkInput?: boolean;
  scrapPct?: number;
  operationIndex?: number | null;
};

export type ApiCreateProductTypeRequest = {
  code?: string;
  name: string;
  operations: ApiProductTypeOperationInput[];
  materials: ApiProductTypeMaterialInput[];
};

export type ApiUpdateProductTypeRequest = {
  name: string;
  status: string;
  operations: ApiProductTypeOperationInput[];
  materials: ApiProductTypeMaterialInput[];
};

export type ApiTechnicalInfo = {
  id: string;
  code: string;
  versionNo: number;
  status: string;
  effectiveFrom: string | null;
  productCode: string;
  productName: string;
  bomHeaderId: string;
  bomCode: string;
  bomVersionNo: number;
  routingHeaderId: string;
  routingCode: string;
  routingVersionNo: number;
  createdAt: string;
  customerName?: string | null;
  productTypeName?: string | null;
  baseBatchQty?: number | null;
  baseBatchUomCode?: string | null;
  operationCount?: number;
  materialCount?: number;
  equipmentCount?: number;
};

export type ApiCreateTechnicalInfoRequest = {
  productCode: string;
  operations?: ApiProductTypeOperationInput[];
  materials?: ApiProductTypeMaterialInput[];
  baseBatchQty?: number | null;
  baseBatchUomCode?: string | null;
  code?: string | null;
};

export type ApiUpdateTechnicalInfoRequest = {
  operations: ApiProductTypeOperationInput[];
  materials: ApiProductTypeMaterialInput[];
  baseBatchQty: number;
  baseBatchUomCode: string;
};

export type ApiCreateProductPackRequest = {
  productCode: string;
  packFormat: string;
  /** Bỏ trống thì backend tự sinh mã FG. */
  fgItemCode?: string | null;
  fgItemName?: string | null;
  lotTracked?: boolean;
  shelfLifeDays?: number | null;
};

export type ApiSetProductStatusRequest = {
  status: string;
};

export type ApiSetPackStatusRequest = {
  status: string;
};

/** ENG BOM */
export type ApiBomLine = {
  id: string;
  lineNo: number;
  componentItemCode: string;
  componentName: string;
  qty: number;
  uomCode: string;
  allowReworkInput: boolean;
  scrapPct: number;
  routingOperationId?: string | null;
  operationCode?: string | null;
  operationName?: string | null;
};

export type ApiBom = {
  id: string;
  code: string;
  versionNo: number;
  status: VersionStatus | string;
  effectiveFrom: string | null;
  scrapFactor: number;
  packFormat: string;
  productCode: string;
  lines: ApiBomLine[];
  /** Standard batch size (FRS-ENG-02 / OI-027). */
  baseBatchQty?: number | null;
  baseBatchUomCode?: string | null;
  batchQtyTolerancePct?: number | null;
};

export type ApiBomLineInput = {
  lineNo: number;
  componentItemCode: string;
  qty: number;
  uomCode: string;
  allowReworkInput?: boolean;
  scrapPct?: number;
  routingOperationId?: string | null;
};

export type ApiCreateBomRequest = {
  code: string;
  productCode: string;
  scrapFactor: number;
  lines: ApiBomLineInput[];
  baseBatchQty?: number | null;
  baseBatchUomCode?: string | null;
  batchQtyTolerancePct?: number | null;
};

export type ApiUpdateBomLinesRequest = {
  lines: ApiBomLineInput[];
  scrapFactor?: number | null;
  baseBatchQty?: number | null;
  baseBatchUomCode?: string | null;
  batchQtyTolerancePct?: number | null;
};

export type ApiOpParam = {
  paramCode: string;
  minValue: number | null;
  target: number | null;
  maxValue: number | null;
  uomLabel: string | null;
  isCcp: boolean;
  dataType?: string;
  sortOrder?: number;
  monitorFrequencyHint?: string | null;
  correctiveActionHint?: string | null;
};

/** ENG Operation Template (catalog — Active/Inactive, not versioned). */
export type ApiOperationTemplate = {
  id: string;
  code: string;
  name: string;
  opType: string;
  capability: string;
  isHoldPoint: boolean;
  requireQcSign: boolean;
  documentMasterCode: string | null;
  equipmentType: string | null;
  defaultLossTypeCode: string | null;
  lossMethod: string | null;
  standardLossRate: number | null;
  tolerancePp: number | null;
  status: string;
  createdAt: string;
  params?: ApiOpParam[] | null;
  sourceSpecCode?: string | null;
  sourceSpecVersion?: number | null;
};

export type ApiCreateOperationTemplateRequest = {
  code: string;
  name: string;
  opType: string;
  capability: string;
  isHoldPoint?: boolean;
  requireQcSign?: boolean;
  documentMasterCode?: string | null;
  equipmentType?: string | null;
  defaultLossTypeCode?: string | null;
  lossMethod?: string | null;
  standardLossRate?: number | null;
  tolerancePp?: number | null;
  params?: ApiOpParam[] | null;
  /** Mã tiêu chuẩn Effective — nạp params mặc định. */
  sourceSpecCode?: string | null;
};

export type ApiUpdateOperationTemplateRequest = {
  name: string;
  opType: string;
  capability: string;
  isHoldPoint?: boolean;
  requireQcSign?: boolean;
  documentMasterCode?: string | null;
  equipmentType?: string | null;
  defaultLossTypeCode?: string | null;
  lossMethod?: string | null;
  standardLossRate?: number | null;
  tolerancePp?: number | null;
  params?: ApiOpParam[] | null;
  sourceSpecCode?: string | null;
};

export type ApiSetEngCatalogStatusRequest = {
  status: string;
};

/** ENG Routing */
export type ApiRoutingOp = {
  id: string;
  seq: number;
  operationCode: string;
  name: string;
  opType: string;
  isHoldPoint: boolean;
  documentMasterCode: string | null;
  equipmentType: string | null;
  lossMethod?: string | null;
  standardLossRate?: number | null;
  tolerancePp?: number | null;
  fixedLossQty?: number | null;
  minLossQty?: number | null;
  defaultLossTypeCode?: string | null;
  capability?: string | null;
  templateCode?: string | null;
  qcPlanId?: string | null;
  qcPlanCode?: string | null;
  params?: ApiOpParam[] | null;
  equipment?: ApiOperationEquipment[] | null;
  checklistPhases?: ApiOperationChecklistPhases | null;
};

export type ApiRouting = {
  id: string;
  code: string;
  versionNo: number;
  status: string;
  effectiveFrom: string | null;
  packFormat: string;
  productCode: string;
  operations: ApiRoutingOp[];
};

export type ApiRoutingOpInput = {
  seq: number;
  operationCode: string;
  name: string;
  opType: string;
  isHoldPoint?: boolean;
  documentMasterCode?: string | null;
  equipmentType?: string | null;
  qcPlanCode?: string | null;
  lossMethod?: string | null;
  standardLossRate?: number | null;
  tolerancePp?: number | null;
  fixedLossQty?: number | null;
  minLossQty?: number | null;
  defaultLossTypeCode?: string | null;
  capability?: string | null;
  templateCode?: string | null;
  params?: ApiOpParam[] | null;
  equipment?: Array<{ code: string; plantCode?: string | null }> | null;
  checklistPhases?: ApiOperationChecklistPhasesInput | null;
};

export type ApiCreateRoutingRequest = {
  code: string;
  productCode: string;
  operations: ApiRoutingOpInput[];
};

export type ApiUpdateRoutingOperationsRequest = {
  operations: ApiRoutingOpInput[];
};

/** ENG Spec */
export type ApiSpecParam = {
  paramCode: string;
  minValue: number | null;
  target: number | null;
  maxValue: number | null;
  uomLabel: string | null;
  isCcp: boolean;
};

export type ApiSpec = {
  id: string;
  code: string;
  name: string;
  versionNo: number;
  status: string;
  effectiveFrom: string | null;
  packFormat: string;
  packFormatLabel?: string | null;
  productCode: string;
  productName?: string | null;
  params: ApiSpecParam[];
};

export type ApiCreateSpecRequest = {
  code: string;
  name: string;
  params: ApiSpecParam[];
  /** Legacy — Spec theo SP+pack. Catalog mới bỏ trống. */
  productCode?: string | null;
  packFormat?: string | null;
  notes?: string | null;
};

export type ApiUpdateSpecDraftRequest = {
  name: string;
  params: ApiSpecParam[];
};

/** ENG QC Plan */
export type ApiQcPlanItem = {
  seq: number;
  checkCode: string;
  prompt: string;
  answerType: string;
  isRequired: boolean;
  defectOnFail: boolean;
  minValue?: number | null;
  maxValue?: number | null;
  step?: number | null;
  uomLabel?: string | null;
  optionsJson?: string | null;
};

export type ApiQcPlan = {
  id: string;
  code: string;
  planType: string;
  /** Category detail id của danh mục `CategoryCodes.PlanType`. */
  planTypeId: string | null;
  name: string;
  versionNo: number;
  status: string;
  effectiveFrom: string | null;
  items: ApiQcPlanItem[];
};

export type ApiCreateQcPlanRequest = {
  code: string;
  planTypeId: string;
  name: string;
  items: ApiQcPlanItem[];
};

export type ApiUpdateQcPlanDraftRequest = {
  name: string;
  items: ApiQcPlanItem[];
  planTypeId?: string | null;
};

/** SYS ApprovalPolicy / inbox */
export type ApiApprovalPolicyStep = {
  stepOrder: number;
  matchRequesterDepartment: boolean;
  allowedRoleCodes: string[];
  /** Empty = any user matching roles. Non-empty = only listed users. */
  allowedUserIds: string[];
};

export type ApiApprovalPolicy = {
  id: string;
  code: string;
  name: string;
  subjectType: string;
  isActive: boolean;
  steps: ApiApprovalPolicyStep[];
};

export type ApiUpsertApprovalPolicyRequest = {
  name: string;
  isActive: boolean;
  steps: ApiApprovalPolicyStep[];
};

export type ApiCreateApprovalPolicyRequest = {
  code: string;
  name: string;
  subjectType: string;
  isActive: boolean;
  steps: ApiApprovalPolicyStep[];
};

export type ApiSetApprovalPolicyActiveRequest = {
  isActive: boolean;
};

export type ApiApprovalInboxItem = {
  instanceId: string;
  instanceStepId: string;
  subjectType: string;
  subjectId: string;
  subjectRef: string;
  status: string;
  stepIndex: number;
  requesterDepartment: string | null;
  policyCode: string | null;
  stepCount: number;
  linesSummary?: string | null;
  totalQty?: number | null;
  uomName?: string | null;
  lineCount?: number;
};

export type ApiApprovalActRequest = {
  comment?: string | null;
};

export type ApiSubjectApprovalSummary = {
  subjectType: string;
  subjectId: string;
  policyEnabled: boolean;
  instanceId?: string | null;
  instanceStatus?: string | null;
  stepIndex?: number | null;
  stepCount?: number | null;
  policyCode?: string | null;
  actorCanAct: boolean;
  requesterDepartment?: string | null;
};

export type ApiApprovalInstanceStepHistory = {
  stepId: string;
  stepIndex: number;
  status: string;
  allowedRoleCodes: string[];
  allowedRoleNames?: string[];
  allowedUserIds: string[];
  allowedUsernames: string[];
  matchRequesterDepartment: boolean;
  actorUsername?: string | null;
  comment?: string | null;
  actedAt?: string | null;
};

export type ApiApprovalInstanceHistory = {
  instanceId: string;
  status: string;
  policyCode: string;
  policyName: string;
  requesterUsername?: string | null;
  requesterDepartment?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  currentStepIndex: number;
  steps: ApiApprovalInstanceStepHistory[];
};

export type ApiSubjectApprovalHistory = {
  policyEnabled: boolean;
  instances: ApiApprovalInstanceHistory[];
};

export type ApiMesWorkQueueItem = {
  operationId: string;
  batchCode: string;
  orderNo: string;
  seq: number;
  operationCode: string;
  opName: string;
  status: string;
  isHoldPoint: boolean;
  blockedReason: string | null;
  productName?: string | null;
  packLabel?: string | null;
  salesOrderNo?: string | null;
  customerName?: string | null;
  lineName?: string | null;
  shiftName?: string | null;
  moQtyLabel?: string | null;
  needBy?: string | null;
  batchLabel?: string | null;
  queuedAt?: string | null;
};

export type ApiMesMyActivityItem = ApiMesWorkQueueItem & {
  startedAt: string | null;
  endedAt: string | null;
  startedByMe: boolean;
  completedByMe: boolean;
};

export type ApiMesOperationInput = {
  itemCode: string;
  itemName?: string | null;
  lotCode: string;
  qty: number;
  uomCode: string;
  locationCode: string | null;
  inventoryTxnNo: string | null;
};

export type ApiMesOperationOutput = {
  outputType: string;
  itemCode: string;
  lotCode: string | null;
  qty: number;
  uomCode: string;
  locationCode: string | null;
  inventoryTxnNo: string | null;
  itemName?: string | null;
};

export type ApiMesRouteStep = {
  id: string;
  seq: number;
  operationCode: string;
  opName: string;
  status: string;
  capability?: string | null;
  opType?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  startedByName?: string | null;
  completedByName?: string | null;
  evaluatedAt?: string | null;
  evaluatedByName?: string | null;
  qcResult?: string | null;
  equipmentName?: string | null;
  goodQty?: number | null;
  scrapQty?: number | null;
};

export type ApiMesBatch = {
  id: string;
  batchCode: string;
  orderNo: string;
  plantCode: string;
  packFormat: string;
  status: BatchStatus | string;
  ebrStatus: string;
  actualStart: string | null;
  actualEnd: string | null;
  operationCount: number;
  completedOps: number;
  lineCode?: string | null;
  lineName?: string | null;
  productCode?: string | null;
  productName?: string | null;
  shiftCode?: string | null;
  shiftName?: string | null;
  openHoldCount?: number;
  operations?: ApiMesRouteStep[] | null;
  /** Plan seq on MO (1..N). 0 = legacy. */
  seq?: number;
  plannedQty?: number;
  uomCode?: string | null;
  uomName?: string | null;
  salesOrderNo?: string | null;
  customerName?: string | null;
  packFormatLabel?: string | null;
};

export type ApiMesBatchStatusBucket = {
  status: string;
  count: number;
};

export type ApiMesBatchListSummary = {
  releasedCount: number;
  issuedCount: number;
  inProgressCount: number;
  holdCount: number;
  completedCount: number;
  statusBuckets: ApiMesBatchStatusBucket[];
};

export type ApiCreateBatchRequest = {
  orderNo: string;
  batchCode?: string | null;
  shiftCode?: string | null;
};

export type ApiMesOperation = {
  id: string;
  batchCode: string;
  seq: number;
  operationCode: string;
  status: string;
  isHoldPoint: boolean;
  documentMasterCode: string | null;
  formInstanceId: string | null;
  clientRequestId: string | null;
  blockedReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  goodQty: number | null;
  scrapQty: number | null;
  inputs: ApiMesOperationInput[];
  outputs: ApiMesOperationOutput[];
  opName?: string | null;
  orderNo?: string | null;
  productName?: string | null;
  packLabel?: string | null;
  salesOrderNo?: string | null;
  customerName?: string | null;
  lineName?: string | null;
  shiftName?: string | null;
  moQtyLabel?: string | null;
  needBy?: string | null;
  batchLabel?: string | null;
  plantCode?: string | null;
  plantName?: string | null;
  route?: ApiMesRouteStep[] | null;
  reworkQty?: number | null;
  wastePct?: number | null;
  reworkPct?: number | null;
  foreignMatter?: boolean;
  linkedInspectionId?: string | null;
  linkedInspectionResult?: string | null;
  qcPlanCode?: string | null;
  qcPlanName?: string | null;
  paramSpecs?: ApiMesParamSpec[] | null;
  paramValues?: ApiMesParamValue[] | null;
  statusHistory?: ApiMesStatusEvent[] | null;
  formStatus?: string | null;
  processLoss?: ApiMesProcessLoss | null;
  opType?: string | null;
  capturePack?: ApiMesCapturePack | null;
  capability?: string | null;
  warnings?: string[] | null;
  batchPlannedQty?: number | null;
  orderPlannedQty?: number | null;
  fgReceiptHint?: ApiMesFgReceiptHint | null;
  ipqcDoneCount?: number;
  ipqcTotalCount?: number;
  checklistPhases?: ApiOperationChecklistPhases | null;
  startedByName?: string | null;
  completedByName?: string | null;
  evaluatedAt?: string | null;
  evaluatedByName?: string | null;
  qcResult?: string | null;
  equipmentName?: string | null;
};

export type ApiMesFgReceiptHint = {
  itemCode: string;
  uomCode: string;
  locationCode: string;
  lotCode: string;
  qty: number;
  itemName?: string | null;
  uomName?: string | null;
};

export type ApiMesCapturePackItem = {
  kind: string;
  code: string;
  label: string;
  required: boolean;
  done: boolean;
  status?: string | null;
  formInstanceId?: string | null;
  isCcp?: boolean;
  /** preStart | preComplete — null for in-progress params. */
  phase?: string | null;
};

export type ApiMesCapturePack = {
  requiredDocCodes: string[];
  preOpFormCodes: string[];
  preOpFormIds: string[];
  params: ApiMesCapturePackItem[];
  qcPlanId?: string | null;
  qcPlanCode?: string | null;
  items: ApiMesCapturePackItem[];
  doneCount: number;
  requiredCount: number;
  readyToComplete: boolean;
  readyToStart?: boolean;
};

export type ApiMesProcessLoss = {
  lossMethod: string | null;
  standardLossRate: number | null;
  tolerancePp: number | null;
  fixedLossQty: number | null;
  minLossQty: number | null;
  defaultLossTypeCode: string | null;
  inputQty: number | null;
  outputQty: number | null;
  lossQty: number | null;
  lossRate: number | null;
  yieldRate: number | null;
  standardLossQty: number | null;
  varianceQty: number | null;
  variancePp: number | null;
  overThreshold: boolean;
};

export type ApiMesParamSpec = {
  paramCode: string;
  label: string;
  minValue?: number | null;
  target?: number | null;
  maxValue?: number | null;
  uomLabel?: string | null;
  isCcp: boolean;
};

export type ApiMesParamValue = {
  paramCode: string;
  value: number;
  isCcp: boolean;
  specState: string;
  recordedAt: string;
  recordedBy?: string | null;
  recordedByName?: string | null;
};

export type ApiMesStatusEvent = {
  fromStatus: string;
  toStatus: string;
  at: string;
  note?: string | null;
};

export type ApiMesStartOperationRequest = {
  clientRequestId?: string | null;
  equipmentCode?: string | null;
};

export type ApiMesPauseOperationRequest = {
  reason: string;
  clientRequestId?: string | null;
};

export type ApiMesResumeOperationRequest = {
  clientRequestId?: string | null;
};

export type ApiMesCancelOperationRequest = {
  reason: string;
  clientRequestId?: string | null;
};

export type ApiMesCompleteOperationRequest = {
  clientRequestId?: string | null;
  goodQty?: number | null;
  scrapQty?: number | null;
  reworkQty?: number | null;
  inputQty?: number | null;
  foreignMatter?: boolean;
  foreignNotes?: string | null;
  wasteOverLimitReason?: string | null;
  qcResult?: string | null;
  createFormIfMapped?: boolean;
  consumes?: ApiMesConsumeLotInput[] | null;
  outputs?: ApiMesProduceLotInput[] | null;
  allowUnderFill?: boolean;
  underFillReason?: string | null;
  lossTypeCode?: string | null;
};

export type ApiBatchLossRecord = {
  id: string;
  batchCode: string;
  operationCode: string;
  seq: number;
  inputQty?: number | null;
  goodQty?: number | null;
  scrapQty?: number | null;
  lossQty?: number | null;
  lossRate?: number | null;
  yieldRate?: number | null;
  standardLossQty?: number | null;
  standardLossRate?: number | null;
  variancePp?: number | null;
  overThreshold: boolean;
  lossTypeCode?: string | null;
  reasonText?: string | null;
  recordedAt: string;
};

export type ApiMesLossListSummary = {
  totalCount: number;
  overCount: number;
  overTodayCount: number;
  withinCount: number;
};

export type ApiEbrProcessLossLine = {
  operationCode: string;
  seq: number;
  inputQty?: number | null;
  goodQty?: number | null;
  lossQty?: number | null;
  lossRate?: number | null;
  standardLossRate?: number | null;
  variancePp?: number | null;
  overThreshold: boolean;
  lossTypeCode?: string | null;
  reasonText?: string | null;
};

export type ApiLossType = {
  id: string;
  code: string;
  name: string;
  createsScrap: boolean;
  isAbnormal: boolean;
  status: string;
  sortOrder: number;
};

export type ApiMesConsumeLotInput = {
  lotCode: string;
  locationCode: string;
  qty: number;
  uomCode: string;
  toLocationCode?: string | null;
  palletCode?: string | null;
  outOfColdAt?: string | null;
  visualOk?: boolean | null;
};

export type ApiMesProduceLotInput = {
  itemCode: string;
  lotCode: string;
  locationCode: string;
  qty: number;
  uomCode: string;
  outputType?: string;
  qcPending?: boolean;
};

export type ApiMesHold = {
  id: string;
  batchCode: string;
  reason: string;
  status: string;
  operationExecutionId: string | null;
};

export type ApiMesHoldStatusBucket = {
  status: string;
  count: number;
};

export type ApiMesHoldListSummary = {
  openCount: number;
  releasedCount: number;
  totalCount: number;
  statusBuckets: ApiMesHoldStatusBucket[];
};

export type ApiQmsStatusBucket = {
  status: string;
  count: number;
};

export type ApiQmsNcrListSummary = {
  openCount: number;
  dispositionPendingCount: number;
  closedCount: number;
  totalCount: number;
  statusBuckets: ApiQmsStatusBucket[];
};

export type ApiQmsDeviationListSummary = {
  openCount: number;
  closedCount: number;
  totalCount: number;
  statusBuckets: ApiQmsStatusBucket[];
};

export type ApiQmsIqcInboxSummary = {
  pendingCount: number;
  withPoCount: number;
  totalCount: number;
  /** Passed + Failed (Đã QC). */
  doneCount?: number;
};

export type ApiQmsFqcInboxSummary = {
  pendingCount: number;
  totalCount: number;
  /** Passed + Failed (Đã QC). */
  doneCount?: number;
};

export type ApiQmsQaReleaseSummary = {
  candidateCount: number;
  blockedByNcrCount: number;
  releasedCount: number;
  rejectedCount: number;
  totalDecisionCount: number;
};

export type ApiMesPlaceHoldRequest = {
  reason: string;
  operationExecutionId?: string | null;
};

export type ApiMesCip = {
  id: string;
  plantCode: string;
  batchCode: string | null;
  reason: string;
  status: string;
  result: string | null;
};

export type ApiMesCipReason = {
  code: string;
  name: string;
};

export type ApiMesCreateCipRequest = {
  plantCode: string;
  batchCode?: string | null;
  reason?: string;
  equipmentCode?: string | null;
};

export type ApiMesHandoff = {
  id: string;
  handoffNo: string;
  batchCode: string;
  fromOperationId: string;
  fromOperationCode: string;
  toOperationId: string | null;
  toOperationCode: string | null;
  lotCode: string;
  itemCode: string;
  qty: number;
  uomCode: string;
  status: string;
  notes: string | null;
  blockedReason: string | null;
  lotQcStatus: string | null;
  completedAt: string | null;
};

export type ApiMesCreateHandoffRequest = {
  fromOperationId: string;
  lotCode: string;
  qty?: number | null;
  notes?: string | null;
  toUserId?: string | null;
};

export type ApiMesReworkOrder = {
  id: string;
  reworkNo: string;
  status: string;
  sourceLotCode: string;
  resultLotCode: string;
  itemCode: string;
  qty: number;
  uomCode: string;
  reason: string;
  batchCode?: string | null;
  orderNo?: string | null;
  locationCode?: string | null;
  createdAt: string;
};

export type ApiMesCreateReworkOrderRequest = {
  sourceLotCode: string;
  qty: number;
  reason: string;
  itemCode?: string | null;
  uomCode?: string | null;
  locationCode?: string | null;
  batchCode?: string | null;
  resultLotCode?: string | null;
};

/** FRS-SYS-06 — runtime nav (W1). */
export type ApiNavShell = "ops" | "field" | "exe";

export type ApiNavItem = {
  screenId: string;
  label: string;
  sortOrder: number;
  placement: string;
};

export type ApiNavGroup = {
  code: string;
  label: string;
  iconKey?: string | null;
  sortOrder: number;
  items: ApiNavItem[];
};

export type ApiNavTreeResponse = {
  shell: string;
  profileVersion: number;
  generatedAt: string;
  groups: ApiNavGroup[];
};

/** FRS-SYS-06 — admin draft (W3). */
export type ApiNavItemDraft = {
  id: string;
  screenId: string;
  labelVi: string | null;
  labelEn: string | null;
  sortOrder: number;
  placement: string;
  isEnabled: boolean;
  requiredPermissions: string[] | null;
  permMatch: string | null;
  rowVersion: string;
};

export type ApiNavGroupDraft = {
  id: string;
  code: string;
  labelVi: string;
  labelEn: string | null;
  iconKey: string | null;
  sortOrder: number;
  isEnabled: boolean;
  rowVersion: string;
  items: ApiNavItemDraft[];
};

export type ApiNavCatalogDraftResponse = {
  shell: string;
  draftVersion: number;
  publishedVersion: number;
  groups: ApiNavGroupDraft[];
};

export type ApiPatchNavGroupRequest = {
  labelVi?: string;
  labelEn?: string | null;
  iconKey?: string | null;
  sortOrder?: number;
  isEnabled?: boolean;
};

export type ApiPatchNavItemRequest = {
  labelVi?: string | null;
  labelEn?: string | null;
  sortOrder?: number;
  placement?: string;
  requiredPermissions?: string[];
  permMatch?: string;
  isEnabled?: boolean;
  groupId?: string;
};

export type ApiNavReorderRequest = {
  groups?: { id: string; sortOrder: number }[];
  items?: { id: string; sortOrder: number; groupId?: string | null }[];
};

export type ApiNavPublishResult = {
  publishedVersion: number;
  contentHash: string;
  publishedAt: string;
};
